import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Input as InputAnt } from "antd";
import { JPInputElementProps } from "@companion-core/shared/app/Components/Inputs/JPInput/JPInputElement";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { JPInputError } from "@companion-core/shared/app/Components/Inputs/JPInputError";
import InputLabel from "@companion-core/web/src/Components/InputLabel";
import InputClearButton from "@companion-core/web/src/Components/InputClearButton";

const Components: Record<string, React.ElementType> = {
  text: InputAnt,
  email: InputAnt,
  number: InputAnt,
  textarea: InputAnt.TextArea,
  budget: InputAnt,
};

export const JPInputElement = ({
  testID,
  type,
  name,
  icon,
  label,
  prefix,
  suffix,
  value,
  disabled,
  maxLength,
  error,
  onChange,
  onBlur,
  onFocus,
  onReset,
  onSubmit,
  placeholder,
  inputAccessibilityLabel,
  tipsId,
  ...props
}: JPInputElementProps): React.ReactElement<JPInputElementProps> => {
  const { t } = useTranslation();
  const Component = useMemo(() => {
    return Components[type];
  }, [type]);

  const inputId = `${name}-input`;
  const errorId = `${name}-error`;
  const isTextArea = type === "textarea";
  const maxLengthHintId = isTextArea && maxLength ? `${name}-max-length-hint` : undefined;
  const describedBy = [tipsId, maxLengthHintId].filter(Boolean).join(" ") || undefined;
  const clearInputAriaLabel = t("common.form.input.actions.clear");
  const shouldShowClear = Boolean(value) && !disabled;
  const textareaAllowClear =
    isTextArea && shouldShowClear
      ? {
          clearIcon: (
            <FontIcon
              name="JP-Circle-close-outline"
              size={24}
              data-testid={`${testID}--clear`}
              aria-label={clearInputAriaLabel}
              onClick={onReset}
            />
          ),
        }
      : undefined;

  return (
    <div className="JPInputElement">
      {label && (
        <InputLabel label={label ?? ""} icon={icon} htmlFor={inputId} id={`${name}-label`} />
      )}
      <Component
        {...props}
        id={inputId}
        placeholder={placeholder}
        status={error ? "error" : undefined}
        data-testid={testID}
        name={name}
        prefix={prefix}
        suffix={
          <span className="JPInputElement__actions">
            {suffix}
            {shouldShowClear && (
              <InputClearButton
                ariaLabel={clearInputAriaLabel}
                testID={testID}
                clearInput={onReset}
              />
            )}
          </span>
        }
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        inputMode={type === "budget" || type === "number" ? "decimal" : undefined}
        autoComplete={type === "email" ? "email" : undefined}
        aria-invalid={!!error}
        {...(describedBy ? { "aria-describedby": describedBy } : {})}
        {...(inputAccessibilityLabel ? { "aria-label": inputAccessibilityLabel } : {})}
        allowClear={textareaAllowClear}
        onChange={onChange}
        onPressEnter={() => onSubmit?.()}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {maxLengthHintId && (
        <span id={maxLengthHintId} className="JPInputElement__sr-only">
          {t("common.form.input.textarea.maxCharacters", { maxLength })}
        </span>
      )}
      {error && <JPInputError inputLabel={label} testID={testID} error={error} id={errorId} />}
    </div>
  );
};
